import { uploadFiles } from "api/uploadApi";
import { CONFIG_OPTION_TOAST_ERROR } from "common/toast";
import { t } from "i18next";
import React, { useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { Input } from "reactstrap";

interface UploadFileProps {
    formik: any,
    fieldValue: string,
    isMultiple: boolean,
    acceptFile?: string,
    isUploadMore?: boolean,
    isDisabled?: boolean,
    maxFile?: number,
    setLinkImage?: any,
};

const UploadFileJobPost = ({
    formik,
    fieldValue = 'files',
    isMultiple = true,
    acceptFile = "image/jpeg, image/png, image/gif, image/webp", // "video/mp4,video/x-m4v,video/*,image/png,image/jpeg,image/*",
    isUploadMore = true,
    isDisabled = false,
    setLinkImage,
}: UploadFileProps) => {
    const [isLoading, setIsLoading] = useState(false);
    const fileInputRef = useRef<HTMLInputElement>(null);
    const pasteAreaRef = useRef<HTMLDivElement>(null);
    const dragItemIndex = useRef<number | null>(null);
    const dragCounterRef = useRef(0);
    const [isPasteAreaFocused, setIsPasteAreaFocused] = useState(false);
    const [isDraggingFiles, setIsDraggingFiles] = useState(false);

    const values = useMemo(() => {
        return formik?.values ? formik?.values?.[`${fieldValue}`] : [];
    }, [formik?.values, fieldValue]);

    const acceptPatterns = useMemo(() => {
        if (!acceptFile) {
            return [] as string[];
        }

        return acceptFile
            .split(",")
            .map((pattern) => pattern.trim())
            .filter(Boolean);
    }, [acceptFile]);

    const isAcceptedFile = (file: File) => {
        if (!acceptPatterns.length) {
            return true;
        }

        const fileType = file.type || "";
        const fileName = (file.name || "").toLowerCase();

        return acceptPatterns.some((pattern) => {
            if (pattern === "*/*") {
                return true;
            }

            if (pattern.endsWith("/*")) {
                const baseType = pattern.split("/")[0];
                return fileType.startsWith(`${baseType}/`);
            }

            if (pattern.startsWith(".")) {
                return fileName.endsWith(pattern.toLowerCase());
            }

            return fileType === pattern;
        });
    };

    const uploadFilesAndUpdate = async (files: File[]) => {
        if (!files?.length) {
            return;
        }

        const allowedFiles = files.filter(isAcceptedFile);
        if (!allowedFiles.length) {
            toast(t("File type not supported"), CONFIG_OPTION_TOAST_ERROR);
            return;
        }

        setIsLoading(true);
        try {
            const formData = new FormData();
            allowedFiles.forEach((file) => {
                formData.append("files", file);
            });

            const resUpload = await uploadFiles(formData);
            if (resUpload?.data) {
                const formatRes = resUpload?.data?.map((item: any) => ({
                    url: item?.url,
                    thumbnailUrl: item?.relativePath,
                    name: item?.fileName,
                    type: item?.contentType?.includes("video") ? "video" : "image",
                }));

                if (formik) {
                    const currentValues = formik?.values?.[`${fieldValue}`] || [];
                    formik.setFieldValue(
                        `${fieldValue}`,
                        [...currentValues, ...formatRes],
                    );
                }
            }
        } catch (error) {
            toast(`${error || ""}`, CONFIG_OPTION_TOAST_ERROR);
            return error;
        } finally {
            setIsLoading(false);
        }
    };

    const handleChangeFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files && files.length > 0) {
            await uploadFilesAndUpdate(Array.from(files));
            if (fileInputRef.current) {
                fileInputRef.current.value = "";
            }
        }
    };

    const handlePaste = async (event: React.ClipboardEvent<HTMLDivElement>) => {
        if (isDisabled) {
            return;
        }

        const clipboardItems = event.clipboardData?.items;
        if (!clipboardItems) {
            return;
        }

        const imageFiles: File[] = [];
        for (let i = 0; i < clipboardItems.length; i++) {
            const item = clipboardItems[i];
            if (item.kind === "file") {
                const file = item.getAsFile();
                if (file && file.type.startsWith("image/")) {
                    imageFiles.push(file);
                }
            }
        }

        if (imageFiles.length) {
            event.preventDefault();
            await uploadFilesAndUpdate(imageFiles);
        }
    };

    const handleDragStart = (event: React.DragEvent<HTMLDivElement>, index: number) => {
        dragItemIndex.current = index;
        event.dataTransfer.effectAllowed = "move";
        event.dataTransfer.setData("text/plain", index.toString());
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.dataTransfer.dropEffect = "move";
    };

    const handleDrop = async (event: React.DragEvent<HTMLDivElement>, index: number) => {
        event.preventDefault();
        if (isDisabled) {
            return;
        }

        if (event.dataTransfer?.files?.length) {
            dragCounterRef.current = 0;
            setIsDraggingFiles(false);
            await uploadFilesAndUpdate(Array.from(event.dataTransfer.files));
            dragItemIndex.current = null;
            return;
        }

        const startIndex = dragItemIndex.current;
        dragItemIndex.current = null;

        if (startIndex === null || startIndex === index) {
            return;
        }

        const files = Array.from(values || []);
        const [movedItem] = files.splice(startIndex, 1);
        files.splice(index, 0, movedItem);
        formik && formik.setFieldValue(`${fieldValue}`, files);
    };

    const handleDragEnd = () => {
        dragItemIndex.current = null;
    };

    const handleContainerDragEnter = (event: React.DragEvent<HTMLDivElement>) => {
        if (isDisabled) {
            return;
        }

        if (event.dataTransfer?.types?.includes("Files")) {
            dragCounterRef.current += 1;
            event.preventDefault();
            setIsDraggingFiles(true);
        }
    };

    const handleContainerDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        if (isDisabled) {
            return;
        }

        if (event.dataTransfer?.types?.includes("Files")) {
            event.preventDefault();
            event.dataTransfer.dropEffect = "copy";
        }
    };

    const handleContainerDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
        if (isDisabled) {
            return;
        }

        if (event.dataTransfer?.types?.includes("Files")) {
            dragCounterRef.current = Math.max(dragCounterRef.current - 1, 0);
            if (dragCounterRef.current === 0) {
                setIsDraggingFiles(false);
            }
        }
    };

    const handleContainerDrop = async (event: React.DragEvent<HTMLDivElement>) => {
        if (isDisabled) {
            return;
        }

        if (event.dataTransfer?.files?.length) {
            event.preventDefault();
            dragCounterRef.current = 0;
            setIsDraggingFiles(false);
            await uploadFilesAndUpdate(Array.from(event.dataTransfer.files));
        }
    };

    const handleDeleteFile = (index: number) => {
        if (isDisabled) {
            return;
        }
        const fileArray = values?.filter((f: any, i: number) => i !== index);
        formik && formik.setFieldValue(`${fieldValue}`, fileArray);
    }

    const formatPositionPreviewImage = (
        files: any,
        activeImage: any,
    ) => {
        const filesFormat = files?.filter((item: any) =>item?.url !== activeImage?.url);

        const data = [activeImage, ...filesFormat];
        return data;
    };

    return (
        <React.Fragment>
            <>
                <div
                    className="d-flex gap-3 flex-wrap"
                    ref={pasteAreaRef}
                    onPaste={handlePaste}
                    tabIndex={isDisabled ? -1 : 0}
                    onClick={() => {
                        if (!isDisabled) {
                            pasteAreaRef.current?.focus();
                        }
                    }}
                    onFocus={() => setIsPasteAreaFocused(true)}
                    onBlur={() => setIsPasteAreaFocused(false)}
                    style={{
                        border: (isPasteAreaFocused || isDraggingFiles) ? "1px solid var(--vz-primary)" : "1px dashed #ced4da",
                        borderRadius: "var(--vz-border-radius)",
                        padding: "12px",
                        backgroundColor: isDisabled
                            ? "#f8f9fa"
                            : isDraggingFiles
                                ? "rgba(13, 110, 253, 0.05)"
                                : "#ffffff",
                        transition: "border-color 0.2s ease, box-shadow 0.2s ease",
                        boxShadow: (isPasteAreaFocused || isDraggingFiles)
                            ? "0 0 0 3px rgba(13, 110, 253, 0.15)"
                            : "none",
                        position: "relative",
                        minHeight: "180px",
                    }}
                    onDragEnter={handleContainerDragEnter}
                    onDragOver={handleContainerDragOver}
                    onDragLeave={handleContainerDragLeave}
                    onDrop={handleContainerDrop}
                >
                    {isDraggingFiles && (
                        <div
                            className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column align-items-center justify-content-center text-primary fw-semibold"
                            style={{
                                pointerEvents: "none",
                                borderRadius: "inherit",
                                background: "rgba(13, 110, 253, 0.08)",
                            }}
                        >
                            <i className="ri-upload-cloud-2-line mb-2" style={{ fontSize: "2rem" }} />
                            {t("DropImagesHere")}
                        </div>
                    )}
                    {!values?.length && !isDraggingFiles && (
                        <div
                            className="position-absolute top-50 start-50 translate-middle text-muted text-center"
                            style={{ pointerEvents: "none" }}
                        >
                            <i className="ri-drag-move-2-line d-block mb-1" style={{ fontSize: "1.5rem" }} />
                            {t("PasteOrDragHint")}
                        </div>
                    )}
                    {(values?.length > 0) && values?.map((_value: any, index: number) => {
                        const url = _value?.url ? _value?.url : (_value && _value);
                        return (
                            <div
                                className="position-relative"
                                key={index}
                                style={{ width: '160px', height: '160px', backgroundColor: '#eaeaea', overflow: 'hidden', borderRadius: 'var(--vz-border-radius)' }}
                                draggable={!isDisabled}
                                onDragStart={(event) => handleDragStart(event, index)}
                                onDragOver={handleDragOver}
                                onDrop={(event) => handleDrop(event, index)}
                                onDragEnd={handleDragEnd}
                            >
                                <img
                                    className="cursor-pointer"
                                    src={url}
                                    alt={`preview-${index + 1}`}
                                    style={{ width: "100%", height: "100%", objectFit: 'cover' }}
                                    onClick={() => {
                                        if (values?.length > 1) {
                                            const urls = formatPositionPreviewImage(values, _value);
                                            setLinkImage && setLinkImage({ urls: urls });
                                        } else {
                                            setLinkImage && setLinkImage({ url: url });
                                        }
                                    }}
                                />
                                <button type="button" className="position-absolute btn btn-sm btn-soft-danger edit-item-btn"
                                    style={{
                                        top: '10px',
                                        right: '10px',
                                        width: '30px',
                                        height: '30px',
                                        borderRadius: '15px',
                                    }}
                                    onClick={() => handleDeleteFile(index)}
                                ><i className="ri-delete-bin-3-fill align-bottom"></i></button>
                            </div>
                        )
                    })}
                    {(!!isUploadMore) && <div className={`custom-file-image-button z-0`}>
                        <Input
                            className="form-control"
                            accept={acceptFile}
                            label="Upload Your Image"
                            data-title="Upload Your Image"
                            id="formSizeDefault"
                            type="file"
                            multiple={isMultiple}
                            disabled={isDisabled}
                            onChange={handleChangeFile}
                            innerRef={fileInputRef}
                        />
                        {<div className="text-upload" style={{ zIndex: 1 }}>  <i className="ri-upload-line me-2" /> {values?.length ? t('Add Image') : t('No file chosen')}</div>}
                    </div>}
                </div>
            </>
        </React.Fragment>
    );
};

export default UploadFileJobPost;
